import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from 'services/api-client';
import { useToast } from '../../contexts/ToastContext';
import { IPlanGroup } from 'types/workout/plan-state';
import { ICommitCycleResponse, IParsedWorkoutCycleResponse } from 'types/workout/parsed-workout-cycle';
import { parsedGroupsToPlanGroups } from 'utils/parsedWorkoutToPlanGroups';
import { useDraftWorkoutsStore } from 'stores/useDraftWorkoutsStore';

export interface CycleDayPreview {
  dayNumber: number;
  instanceName: string;
  personalizedNotes: string;
  groups: IPlanGroup[];
}

export interface CyclePreviewState {
  cycleName: string;
  durationWeeks: number;
  days: CycleDayPreview[];
}

/**
 * Hook que orquesta el flujo `Importar ciclo con IA`:
 *  - `parseFile(file)` / `parseText(text)`: llama al backend, transforma la
 *    respuesta a un preview editable en memoria.
 *  - `updateCycleName` / `updateDurationWeeks` / `updateDayName` / `removeDay`:
 *    mutaciones puntuales del preview.
 *  - `commit()`: valida que todos los ejercicios esten matcheados y dispara la
 *    creacion atomica (N WorkoutTemplate + 1 TrainingCycleTemplate). Limpia el
 *    preview en exito y lo preserva en error para que el coach pueda retry.
 */
export const useImportWorkoutCycleAI = () => {
  const { showToast } = useToast();
  const [parsing, setParsing] = useState<boolean>(false);
  const [committing, setCommitting] = useState<boolean>(false);
  const [preview, setPreview] = useState<CyclePreviewState | null>(null);

  // Persistencia del preview en localStorage: si el coach refresca despues
  // de un parse caro (~20-45s + costo OpenAI) no pierde el laburo.
  const saveDraftAction = useDraftWorkoutsStore((s) => s.saveDraft);
  const deleteDraftAction = useDraftWorkoutsStore((s) => s.deleteDraft);
  // Selector de referencia estable + memo para evitar infinite loop:
  // si el selector creara un array nuevo en cada render, Zustand lo detecta
  // como cambio y rerendereia → loop.
  const allDrafts = useDraftWorkoutsStore((s) => s.drafts);
  const cycleDrafts = useMemo(() => allDrafts.filter((d) => d.kind === 'cycle'), [allDrafts]);
  const currentDraftIdRef = useRef<string | null>(null);

  // Auto-save debounced: cuando el preview cambia, persistir.
  useEffect(() => {
    if (!preview) return;
    const handle = setTimeout(() => {
      const id = saveDraftAction({
        kind: 'cycle',
        id: currentDraftIdRef.current ?? undefined,
        source: 'ai',
        cycleName: preview.cycleName,
        durationWeeks: preview.durationWeeks,
        days: preview.days
      });
      currentDraftIdRef.current = id;
    }, 500);
    return () => clearTimeout(handle);
  }, [preview, saveDraftAction]);

  const buildPreview = useCallback(
    (parsed: IParsedWorkoutCycleResponse): CyclePreviewState => ({
      cycleName: parsed.cycleName ?? 'Nuevo ciclo',
      // Default 4 semanas (mensual). El coach lo puede editar antes del commit.
      durationWeeks: 4,
      days: (parsed.days ?? []).map((d) => ({
        dayNumber: d.dayNumber,
        instanceName: d.instanceName,
        personalizedNotes: d.personalizedNotes ?? '',
        groups: parsedGroupsToPlanGroups(d.groups)
      }))
    }),
    []
  );

  const handleParseResponse = useCallback(
    (response: unknown) => {
      const parsed = ((response as { data?: unknown }).data ?? response) as IParsedWorkoutCycleResponse;
      const next = buildPreview(parsed);
      setPreview(next);
      showToast('success', 'Ciclo importado', `${next.days.length} día(s) detectados`);
      return next;
    },
    [buildPreview, showToast]
  );

  const parseFile = useCallback(
    async (file: File): Promise<CyclePreviewState | null> => {
      setParsing(true);
      try {
        const response = await api.workout.parseWorkoutCycleFromAI({ file });
        return handleParseResponse(response);
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Error desconocido';
        showToast('error', 'No se pudo importar el ciclo', detail);
        return null;
      } finally {
        setParsing(false);
      }
    },
    [handleParseResponse, showToast]
  );

  const parseText = useCallback(
    async (text: string): Promise<CyclePreviewState | null> => {
      if (!text || text.trim().length === 0) {
        showToast('warn', 'Texto vacío', 'Pega o escribe la planificación');
        return null;
      }
      setParsing(true);
      try {
        const response = await api.workout.parseWorkoutCycleFromAI({ text });
        return handleParseResponse(response);
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Error desconocido';
        showToast('error', 'No se pudo importar el ciclo', detail);
        return null;
      } finally {
        setParsing(false);
      }
    },
    [handleParseResponse, showToast]
  );

  const updateCycleName = useCallback((name: string) => {
    setPreview((prev) => (prev ? { ...prev, cycleName: name } : prev));
  }, []);

  const updateDurationWeeks = useCallback((weeks: number) => {
    setPreview((prev) => (prev ? { ...prev, durationWeeks: Math.max(1, Math.min(52, weeks)) } : prev));
  }, []);

  const updateDayName = useCallback((idx: number, name: string) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const days = prev.days.map((d, i) => (i === idx ? { ...d, instanceName: name } : d));
      return { ...prev, days };
    });
  }, []);

  const removeDay = useCallback((idx: number) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const days = prev.days.filter((_, i) => i !== idx);
      return { ...prev, days };
    });
  }, []);

  /**
   * Resuelve un ejercicio del preview (por uuid interno) asignando un id real
   * del catalogo + nombre canonico. Usado por el dialog cuando el coach elige
   * o crea el ejercicio que faltaba.
   */
  const resolveExercise = useCallback(
    (dayIdx: number, exerciseInternalId: string | number, catalogId: number, name: string) => {
      setPreview((prev) => {
        if (!prev) return prev;
        const days = prev.days.map((d, i) => {
          if (i !== dayIdx) return d;
          return {
            ...d,
            groups: d.groups.map((g) => ({
              ...g,
              exercises: g.exercises.map((ex) =>
                ex.id === exerciseInternalId ? { ...ex, exercise: { id: catalogId, name } } : ex
              )
            }))
          };
        });
        return { ...prev, days };
      });
    },
    []
  );

  const reset = useCallback(() => {
    if (currentDraftIdRef.current) {
      deleteDraftAction(currentDraftIdRef.current);
      currentDraftIdRef.current = null;
    }
    setPreview(null);
  }, [deleteDraftAction]);

  /**
   * Carga un cycle draft persistido (sobrevive un refresh accidental).
   * Util cuando el coach refresca durante el parse: el preview vuelve
   * intacto y se evita re-pagar la llamada a OpenAI.
   */
  const restoreFromDraft = useCallback(
    (draftId: string) => {
      const draft = cycleDrafts.find((d) => d.id === draftId);
      if (!draft) return false;
      setPreview({
        cycleName: draft.cycleName,
        durationWeeks: draft.durationWeeks,
        days: draft.days
      });
      currentDraftIdRef.current = draft.id;
      return true;
    },
    [cycleDrafts]
  );

  const commit = useCallback(async (): Promise<ICommitCycleResponse | null> => {
    if (!preview) return null;
    if (preview.days.length === 0) {
      showToast('warn', 'Ciclo vacío', 'Agregá al menos un día antes de guardar');
      return null;
    }
    // Validacion: todos los ejercicios deben tener un id real del catalogo
    const unresolved: string[] = [];
    for (const d of preview.days) {
      for (const g of d.groups) {
        for (const ex of g.exercises) {
          if (ex.exercise.id == null) {
            unresolved.push(`${d.instanceName} → ${ex.exercise.name || 'sin nombre'}`);
          }
        }
      }
    }
    if (unresolved.length > 0) {
      showToast(
        'warn',
        'Faltan ejercicios',
        `Asigná o creá el ejercicio para: ${unresolved.slice(0, 3).join(', ')}${unresolved.length > 3 ? '…' : ''}`
      );
      return null;
    }

    setCommitting(true);
    try {
      const payload = {
        cycleName: preview.cycleName,
        durationWeeks: preview.durationWeeks,
        workouts: preview.days.map((d) => ({
          dayNumber: d.dayNumber,
          instanceName: d.instanceName,
          personalizedNotes: d.personalizedNotes || undefined,
          groups: d.groups.map((g) => ({
            groupNumber: g.groupNumber,
            name: g.name,
            isRestPeriod: g.isRestPeriod ?? false,
            groupType: g.groupType,
            exercises: g.exercises.map((ex) => ({
              exercise: { id: ex.exercise.id as number },
              rowIndex: ex.rowIndex,
              repetitions: ex.repetitions || null,
              sets: ex.sets || null,
              weight: ex.weight || null,
              restInterval: ex.restInterval || null,
              tempo: ex.tempo || null,
              notes: ex.notes || null,
              difficulty: ex.difficulty || null,
              duration: ex.duration || null,
              distance: ex.distance || null,
              setConfiguration: ex.setConfiguration ?? null
            }))
          }))
        }))
      };
      const response = await api.workout.commitWorkoutCycle(payload);
      const result = ((response as { data?: unknown }).data ?? response) as ICommitCycleResponse;
      showToast('success', 'Ciclo creado', `${result.workoutTemplateIds.length} workout(s) + 1 ciclo guardados`);
      // Cleanup del draft persistido — el cycle ya esta en el backend.
      if (currentDraftIdRef.current) {
        deleteDraftAction(currentDraftIdRef.current);
        currentDraftIdRef.current = null;
      }
      setPreview(null);
      return result;
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Error desconocido';
      showToast('error', 'No se pudo crear el ciclo', detail);
      return null;
    } finally {
      setCommitting(false);
    }
  }, [preview, showToast, deleteDraftAction]);

  return {
    parsing,
    committing,
    preview,
    pendingCycleDrafts: cycleDrafts,
    restoreFromDraft,
    parseFile,
    parseText,
    updateCycleName,
    updateDurationWeeks,
    updateDayName,
    removeDay,
    resolveExercise,
    commit,
    reset
  };
};
